/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Group from '@linc-technologies/linced-common/models/group';
import { get, computed } from '@ember/object';
import { filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import { fragment } from 'ember-data-model-fragments/attributes';
import EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import {
  AttendanceParams,
  AutoGroupTypes,
  CalculatedParams,
  JurisdictionalParams,
} from '@linc-technologies/linced-common/enums/group-auto-groups';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import MembershipStatus from '@linc-technologies/linced-common/enums/membership-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import type AutogroupParams from '@linc-technologies/linced-common/models/autogroup-params';
import type Field from '@linc-technologies/linced-common/models/people-schema/field';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import type SessionService from '@linc-technologies/linced-common/services/session';
import {
  filterBy as filterByArray,
  findBy,
  mapBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import type Person from 'linced-app/models/person';
import type SchoolSchemaService from 'linced-app/services/school-schema';

// Proto: groups.Group.
export default class _Group extends Group {
  declare [Type]: 'group';

  // Services, i.e. required for autogroup "based on" computed property.
  @service
  declare session: SessionService;

  @service
  declare options: OptionsService;

  @service
  declare schoolSchema: SchoolSchemaService;

  // School's teams, used for autogroup "based on" computed property.
  @optionAlias('core:groups:teams')
  teams: unknown;

  @filterBy('teams', 'value')
  declare cleanTeams: unknown[];

  // Alias the school for easier use in "based on" computed property.
  @alias('schoolId')
  declare school: unknown;

  @fragment('autogroup-params')
  declare params: AutogroupParams | null;

  @filterBy('staff', 'status', MembershipStatus.CURRENT)
  declare currentStaff: unknown[];

  @computed('currentStaff.@each.person')
  get currentStaffPeople() {
    return mapBy(this.currentStaff, 'person');
  }

  @computed('currentStaffPeople.@each.fullName')
  get staffNames() {
    const names = mapBy(this.currentStaff, 'person.fullName');
    return names.join(', ');
  }

  @filterBy('caregivers', 'status', MembershipStatus.CURRENT)
  declare currentCaregivers: unknown[];

  learnersSortOrder = Object.freeze(['person.firstName', 'person.lastName']);

  get filteredLearners() {
    return this.learners.filter((learner) => {
      return !isEmpty(learner.person);
    });
  }

  get sortedLearners() {
    return sortBy(this.filteredLearners, ...this.learnersSortOrder);
  }

  get currentLearners() {
    return filterByArray(
      this.filteredLearners,
      'status',
      MembershipStatus.CURRENT,
    );
  }

  // The triangulation page reads this list many times in one render. Keep
  // the sorted result until the learners change.
  @cached
  get sortedCurrentLearners() {
    return sortBy(this.currentLearners, ...this.learnersSortOrder);
  }

  get currentEnrolledLearners() {
    return this.currentLearners.filter(
      // Member.person gives the common Person. enrolmentStatus is on this
      // app's person (WarpDrive types eachAttribute against keyof this, so
      // the subclass can not narrow the base type).
      (learner) =>
        (learner.person as Person).enrolmentStatus === EnrolmentStatus.CURRENT,
    );
  }

  get currentLearnerMembers() {
    return filterByArray(this.learners, 'status', MembershipStatus.CURRENT);
  }

  get currentLearnerMemberIds() {
    return mapBy(this.currentLearnerMembers, 'personId');
  }

  @tracked currentYear = moment().year();

  @computed('currentYear', 'label', 'year')
  get labelWithYear() {
    // A year of 0 (the Go zero value) means no year.
    if (!this.year || this.year === this.currentYear) {
      return this.label;
    }
    return `${this.label} (${this.year})`;
  }

  @computed(
    'type',
    'autoGroupType',
    'params.{team,threshold,jurisdiction,rangeStart,rangeEnd,type,field,value}',
    'cleanTeams.@each.{value,label}',
    'schoolSchema.schemaFetched',
  )
  get basedOn() {
    let basedOn: string | null = '';
    if (this.type !== GroupType.AUTO) {
      // Not an autogroup; not "based on" anything.
      return basedOn;
    }

    let val: string, entity: unknown;
    switch (this.autoGroupType) {
      case 1: // Team: Not in enum as they're auto-generated/managed.
        val = this.get('params.team') as string;
        entity = findBy(this.cleanTeams, 'value', val);
        if (!isEmpty(entity)) {
          basedOn = get(entity, 'label') as string;
        } else {
          basedOn = val;
        }
        break;
      case AutoGroupTypes.JURISDICTIONAL:
        basedOn = JurisdictionalParams.getLabel(this.get('params.type'));
        break;
      case AutoGroupTypes.ATTENDANCE:
        basedOn = AttendanceParams.getLabel(this.get('params.type'));
        break;
      case AutoGroupTypes.FIELD:
        val = this.get('params.field') as string;
        if (this.get('schoolSchema.schemaFetched')) {
          // Peeking ourselves rather than using schoolSchema.getField to
          // avoid the PromiseProxy which it returns.
          entity = this.store
            .peekAll<Field>('people-schema/field')
            .filter((field) => {
              return (
                field.key === val &&
                field.personType === PersonType.LEARNER &&
                field.get('school.id') === this.get('school.id')
              );
            })[0];
          if (!isEmpty(entity)) {
            basedOn = get(entity, 'label') as string;
          } else {
            basedOn = val;
          }
        } else {
          // Returning the field key for now.
          basedOn = val;
        }
        break;
      case 5: // Page: Not in enum as they're auto-generated/managed.
        if (!isEmpty(this.get('params.value'))) {
          entity = this.store.peekRecord(
            'posts/page',
            this.get('params.value') as string,
          );
          if (!isEmpty(entity)) {
            basedOn = get(entity, 'title') as string;
          }
        }
        break;
      case AutoGroupTypes.CALCULATED:
        basedOn = CalculatedParams.getLabel(this.get('params.type'));
        break;
      case AutoGroupTypes.CUSTOM:
        // Not implemented.
        break;
    }

    return basedOn;
  }
}
